// #############################################################################
// #
// #  Spanish translations
// #
// #############################################################################
// This file contains the Spanish translations for the common components of the application.
// It is used across different parts of the application to ensure consistency in language and terminology.
// It is also used to provide a localized experience for spanish-speaking users.
// It includes translations for common phrases, error messages, and UI elements that are used throughout the
// application.
// This helps in making the application more user-friendly and accessible to spanish-speaking audiences.
// It is important to keep this file updated with any new phrases or changes in the application to ensure that
// the translations remain accurate and relevant.
// #############################################################################

export const langEs = {
  home: {
    welcome: 'Bienvenido al servicio {service}',
    enter: 'Entrar'
  },
  account_type: {
    publisher: 'Editor',
    consumer: 'Agregador',
    author: 'Autor'
  },
  app: {
    accountNotSubscribedToCurrentService: 'Tu cuenta no está suscrita al servicio actual'
  },
  login: {
    continue: 'Continuar',
    login_tab: 'Iniciar sesión',
    logout: 'Cerrar sesión',
    register_tab: 'Registrarse',
    loginTitle: 'Inicia sesión en tu cuenta',
    chooseSignInMethod: 'Elige cómo iniciar sesión',
    noAccount: '¿No tienes una cuenta?',
    registerButton: 'Crear una cuenta',
    cancel: 'Cancelar',
    identifier: 'Correo electrónico',
    identifierDescription: 'Puedes conectarte usando tu correo electrónico',
    identifierPlaceholder: 'Ingresa tu correo electrónico',
    usernameOrEmail: 'Usuario o Correo electrónico',
    useAnotherAccount: 'Usar otra cuenta',
    username: 'Nombre de usuario',
    password: 'Contraseña',
    email: 'Correo electrónico',
    loginButton: 'Iniciar sesión',
    signingIn: 'Iniciando sesión...',
    signInWithEmail: 'Iniciar sesión con correo electrónico',
    signInWithEmailAgain: 'Solicitar un nuevo enlace de inicio de sesión por correo electrónico',
    signInWithEmailDescription: 'Usa el correo electrónico de tu cuenta',
    signingInWithMagicLink: 'Iniciando sesión con enlace mágico...',
    signInWithEmailMessageSent: 'Un enlace mágico ha sido enviado a tu email, sigue el enlace para iniciar sesión.',
    signingInWithEmail: 'Iniciando sesión con correo electrónico...',
    subscribeButton: 'Suscribirse a {type}',
    registerAsButton: 'Registrarse como {type}',
    showPassword: 'Mostrar contraseña',
    hidePassword: 'Ocultar contraseña',
    or: 'o',
    subscriptionSuccess: 'Suscripción exitosa',
    alternativeAccountDescription: 'Tu cuenta está actualmente no suscrita al servicio actual. Puedes suscribirte o cambiar a los siguientes servicios.',
    alternativeAccountGoTo: 'Ir a {type}',
    sameAccount: 'Ya estás conectado con la misma cuenta.',
    sessionConflict: {
      title: 'Conflicto de sesión',
      message: 'Ya hay una sesión abierta para otra cuenta.',
      currentSession: 'Sesión actual:',
      magicLinkSession: 'Sesión de enlace mágico:',
      question: '¿Quieres permanecer conectado con la sesión actual o conectarte con la sesión de enlace mágico?',
      stayWithCurrent: 'Permanecer con la sesión actual',
      reconnectWithMagicLink: 'Conectarse con la sesión de enlace mágico'
    },
    error: {
      invalidCredentials: 'Credenciales inválidas',
      loginFailed: 'Inicio de sesión fallido',
      apiConnection: 'Error de conexión a la API',
      subscribeFailed: 'Registro fallido: {error}',
      signInWithEmailFailed: 'Inicio de sesión con correo electrónico fallido: {error} (verifica tu correo electrónico o nombre de usuario).',
      invalidEmailFormat: 'Por favor, ingrese una dirección de correo electrónico válida'
    },
    alternativeAccount: {
      contentCreators: '¿Eres un creador de contenido?',
      contentCreatorsLink: 'Inicia sesión aquí',
      authors: '¿Eres un autor?',
      authorsLink: 'Inicia sesión aquí',
      aggregators: '¿Eres un agregador?',
      aggregatorsLink: 'Inicia sesión aquí',
      author: 'Autor',
      consumer: 'Agregador',
      publisher: 'Editor'
    }
  },
  registrationType: {
    publisher: {
      introduction: {
        features: {
          feature1: '',
          feature5: ''
        }
      }
    },
    consumer: {
      introduction: {
        features: {
          feature1: '',
          feature5: ''
        }
      }
    },
    author: {
      introduction: {
        features: {
          feature1: '',
          feature5: ''
        }
      }
    }
  },
  registration: {
    title: 'Registro de Cuenta',
    introduction: {
      title: 'Bienvenido al Registro',
      subtitle: 'Registrarse para el Servicio {service}',
      description: 'Está a punto de registrarse para el servicio {service}. Este registro le permitirá acceder a todas las funciones y capacidades disponibles para las cuentas {service}.',
      startRegistration: 'Iniciar Registro',
      alreadyHaveAccount: '¿Ya tiene una cuenta? Iniciar sesión'
    },
    step_introduction: {
      title: 'Introducción'
    },
    step_account_setup: {
      title: 'Configuración del Perfil',
      displayName: 'Nombre de Mostrar',
      displayNamePlaceholder: 'Ingresa tu nombre de mostrar',
      username: 'Nombre de usuario',
      usernamePlaceholder: 'Ingresa tu nombre de usuario',
      confirmPassword: 'Confirmar Contraseña',
      passwordsDoNotMatch: 'Las contraseñas no coinciden',
      password: 'Contraseña',
      email: 'Correo electrónico',
      emailPlaceholder: 'Ingresa tu dirección de correo electrónico',
      affiliation: 'Afiliación',
      affiliationPlaceholder: 'Universidad, Empresa o Institución',
      homePage: 'Página de Inicio / Página Principal',
      homePagePlaceholder: 'https://tu-sitio-web.com',
      submit: 'Enviar Perfil',
      submitting: 'Enviando...'
    },
    step_account_verification: {
      title: 'Verificación de Cuenta',
      tokenLabel: 'Token de Verificación',
      tokenPlaceholder: 'Ingresa el token',
      checkEmail: 'Hemos enviado un correo electrónico de verificación a tu dirección. Por favor revisa tu bandeja de entrada y sigue las instrucciones para verificar tu cuenta.',
      tokenSent: 'Token de verificación enviado a: {email}',
      resendToken: 'Reenviar Token',
      verifyAccount: 'Verificar Cuenta',
      sendingEmail: 'Enviando Correo Electrónico...',
      resendEmail: 'Reenviar Correo Electrónico',
      accountVerified: '¡Cuenta verificada exitosamente!',
      redirecting: 'Redirigiendo al siguiente paso...',
      verifying: 'Verificar Activación',
      sendingToken: 'Enviando token de verificación...',
      invalidToken: 'Token de verificación inválido. Por favor, inténtalo de nuevo.',
      tooManyAttempts: 'Demasiados intentos fallidos. Por favor solicita un nuevo token.',
      noToken: 'No se encontró token de verificación. Por favor solicita un nuevo token.',
      verificationSuccess: '¡Verificación de correo electrónico exitosa!',
      attemptsRemaining: '{count} intentos restantes',
      resendSuccess: 'Nuevo token de verificación enviado a tu correo electrónico.',
      networkError: 'Error de red. Por favor verifica tu conexión e inténtalo de nuevo.',
      checkingActivation: 'Verificando activación de cuenta...',
      emailSentTo: 'Correo electrónico enviado a:',
      activationEmailResent: '¡Correo electrónico de activación enviado exitosamente! Por favor revisa tu bandeja de entrada.',
      resendFailed: 'Error al enviar el correo electrónico de activación. Por favor, inténtalo de nuevo.',
      missingIdentifierOrRegistrationToken: 'Falta identificador o token de registro',
      accountDoesNotHaveAuthorPrivileges: 'La cuenta no tiene privilegios de autor. Por favor contacta al soporte.',
      accountActivationCheckFailed: 'Verificación de activación de cuenta fallida. Por favor, inténtalo de nuevo.',
      accountNotYetActivated: 'Cuenta aún no activada. Por favor revisa tu correo electrónico y haz clic en el enlace de activación.',
      invalidCredentials: 'Credenciales inválidas. Por favor verifica tu nombre de usuario y contraseña.',
      failedToVerifyAccountActivation: 'Error al verificar la activación de la cuenta. Por favor, inténtalo de nuevo.',
      failedToResendActivationEmail: 'Error al reenviar el correo electrónico de activación. Por favor, inténtalo de nuevo.',
      accountRegistrationRejected: 'Registro de cuenta rechazado. Por favor contacta al soporte.'
    },
    step_registration_complete: {
      title: 'Completar Registro',
      registrationSummary: 'Resumen del Registro',
      profileCreated: 'Perfil creado',
      emailsSent: 'Correos electrónicos enviados a editores',
      continueToDashboard: 'Continuar al Panel de Control',
      continueToLogin: 'Continuar al Inicio de Sesión'
    }
  },
  common: {
    cancel: 'Cancelar',
    save: 'Guardar'
  },
  navbar: {
    docs: 'Documentos',
    account: 'Cuenta',
    settings: 'Configuración',
    logout: 'Cerrar sesión',
    login: 'Iniciar sesión',
    pendingRegistrationDesc: 'Por favor, completa tu registro para acceder a este servicio.',
    apps: {
      title: 'Aplicaciones',
      publisher: 'Editor',
      publisherDesc: 'Cambiar al servicio de editor',
      author: 'Autor',
      authorDesc: 'Cambiar al servicio de autor',
      consumer: 'Agregador',
      consumerDesc: 'Cambiar al servicio de agregador',
      website: 'Sitio web',
      websiteDesc: 'Cambiar al sitio web',
      portal: 'Portal',
      portalDesc: 'Cambiar al portal',
      currentApplicationDesc: 'Servicio actual',
      unavailableServiceDesc: 'Servicio no disponible'
    }
  },
  sidebar: {
    expand: 'Expandir',
    collapse: 'Contraer',
    info: 'Información',
    infoDesc: 'Información sobre el servicio',
    showDescriptions: 'Mostrar descripciones',
    hideDescriptions: 'Ocultar descripciones'
  },
  pagination: {
    previous: 'Anterior',
    next: 'Siguiente',
    first: 'Primera',
    last: 'Última',
    page: 'Página {current} de {total}',
    itemsPerPage: 'Elementos por página'
  },
  table: {
    loading: 'Cargando...',
    noData: 'Sin datos',
    noResults: 'Sin resultados',
    search: 'Buscar'
  },
  form: {
    submit: 'Enviar',
    cancel: 'Cancelar',
    save: 'Guardar',
    loading: 'Cargando...'
  },
  modal: {
    close: 'Cerrar',
    confirm: 'Confirmar',
    cancel: 'Cancelar',
    save: 'Guardar'
  },
  alert: {
    success: 'Éxito',
    error: 'Error',
    warning: 'Advertencia',
    info: 'Información'
  },
  toast: {
    success: 'Éxito',
    error: 'Error',
    warning: 'Advertencia',
    info: 'Información',
    close: 'Cerrar'
  },
  tooltip: {
    success: 'Éxito',
    error: 'Error',
    warning: 'Advertencia',
    info: 'Información'
  },
  notification: {
    success: 'Éxito',
    error: 'Error',
    warning: 'Advertencia',
    info: 'Información'
  },
  notifications: {
    title: 'Notificaciones',
    empty: 'No hay notificaciones',
    loading: 'Cargando notificaciones...',
    pageInfo: 'Página {current} de {total}',
    error: 'Error al cargar notificaciones',
    markAsRead: 'Marcar como leído',
    markAsUnread: 'Marcar como no leído',
    open: 'Abrir',
    markAllAsRead: 'Marcar todas como leídas',
    unread: 'No leídas',
    all: 'Todas',
    noContent: 'No hay contenido disponible',
    close: 'Cerrar',
    reload: 'Recargar notificaciones',
    showMore: 'Mostrar más',
    loadingMore: 'Cargando más notificaciones...',
    errorLoadingMore: 'Error al cargar más notificaciones',
    noMoreNotifications: 'No hay más notificaciones',
    noNotifications: 'No hay notificaciones',
    noNotificationsFound: 'No se encontraron notificaciones'
  },
  account: {
    title: 'Información de la cuenta',
    profile_section: 'Perfil',
    api_keys_section: 'Claves API',
    security_section: 'Seguridad',
    loading: 'Cargando información de la cuenta...',
    profile: 'Perfil',
    authorProfile: 'Perfil de autor',
    publisherProfile: 'Perfil de editor',
    aggregatorProfile: 'Perfil de agregador',
    id: 'ID',
    name: 'Nombre',
    email: 'Correo electrónico',
    services: 'Servicios',
    memberSince: 'Miembro desde',
    lastLogin: 'Último acceso',
    createNewApiKey: 'Crear nueva clave API',
    apiKeysTitle: 'Claves API',
    apiKeys: {
      name: 'Nombre',
      keyId: 'ID de clave',
      status: 'Estado',
      lastUsed: 'Último uso',
      created: 'Creado',
      expires: 'Expira',
      neverUsed: 'Nunca usado',
      noKeys: 'No se encontraron claves API.',
      advancedControls: 'Para controles avanzados, visita',
      advancedControlsLink: 'la página de gestión de la cuenta'
    },
    security: {
      changePassword: 'Cambiar contraseña',
      changePasswordDescription: 'Actualice la contraseña de su cuenta. Debe ingresar su contraseña actual para confirmar el cambio.',
      currentPassword: 'Contraseña actual',
      currentPasswordPlaceholder: 'Ingrese su contraseña actual',
      newPassword: 'Nueva contraseña',
      newPasswordPlaceholder: 'Ingrese su nueva contraseña',
      confirmPassword: 'Confirmar nueva contraseña',
      confirmPasswordPlaceholder: 'Vuelva a ingresar su nueva contraseña',
      changePasswordButton: 'Cambiar contraseña',
      changingPassword: 'Cambiando...',
      passwordChangedSuccess: 'Su contraseña ha sido cambiada correctamente.',
      passwordChangeFailed: 'Error al cambiar la contraseña. Verifique su contraseña actual e intente de nuevo.',
      resetPassword: 'Restablecer contraseña',
      resetPasswordDescription: '¿Olvidó su contraseña? Podemos enviar un enlace de restablecimiento a su correo registrado. Haga clic en el enlace del correo para establecer una nueva contraseña.',
      sendResetEmailButton: 'Enviar correo de restablecimiento',
      sendingResetEmail: 'Enviando...',
      resetEmailSent: 'Se ha enviado un correo de restablecimiento de contraseña a su dirección registrada. Revise su bandeja de entrada y siga el enlace para establecer una nueva contraseña.',
      resetPasswordFailed: 'Error al enviar el correo de restablecimiento. Intente de nuevo.',
      changePrimaryEmail: 'Cambiar correo principal',
      changePrimaryEmailDescription: 'Actualice su dirección de correo principal. Se enviará un correo de verificación a la nueva dirección. El nuevo correo solo se activará después de verificarlo.',
      newEmail: 'Nuevo correo',
      newEmailPlaceholder: 'Ingrese su nueva dirección de correo',
      confirmEmail: 'Confirmar nuevo correo',
      confirmEmailPlaceholder: 'Vuelva a ingresar su nueva dirección de correo',
      emailsDoNotMatch: 'Las direcciones de correo no coinciden',
      changeEmailButton: 'Enviar correo de verificación',
      sendingVerification: 'Enviando...',
      emailVerificationSent: 'Se ha enviado un correo de verificación a su nueva dirección. Revise su bandeja de entrada y siga las instrucciones para activar el nuevo correo.',
      emailChangeFailed: 'Error al solicitar el cambio de correo. Intente de nuevo.'
    }
  },
  profileSetup: {
    displayName: 'Nombre de Mostrar',
    displayNamePlaceholder: 'Ingrese su nombre de mostrar',
    displayNameHelp: 'Su nombre de mostrar se utilizará para identificarlo en el sistema (este no es su nombre de usuario).',
    title: 'Configuración del Perfil',
    description: 'Por favor, proporcione su información profesional para completar su perfil.',
    affiliation: 'Afiliación',
    affiliationPlaceholder: 'ej., Universidad de Cambridge, MIT, Google Research',
    areaOfInterest: 'Área de Interés',
    areaOfInterestPlaceholder: 'ej., Machine Learning, Visión por Computadora, NLP',
    homePage: 'Página de Inicio',
    homePagePlaceholder: 'https://su-sitio-web.com',
    secondaryEmails: 'Correos Secundarios',
    addSecondaryEmail: 'Agregar Correo Secundario',
    secondaryEmailPlaceholder: 'su correo electrónico',
    secondaryEmailsHelp: 'Sus correos secundarios',
    save: 'Guardar Perfil',
    saving: 'Guardando...',
    nextPage: 'Página Siguiente',
    update: 'Actualizar Perfil',
    updating: 'Actualizando...',
    avatarUrl: 'URL del avatar',
    avatarUrlPlaceholder: 'https://ejemplo.com/avatar.jpg',
    changeAvatar: 'Cambiar avatar',
    noAvatar: 'Sin avatar',
    success: {
      saved: '¡Perfil guardado exitosamente!'
    },
    errors: {
      affiliationRequired: 'La afiliación es requerida.',
      areaOfInterestRequired: 'El área de interés es requerida.',
      invalidHomePage: 'Por favor, ingrese una URL válida para su página de inicio.',
      tooManySecondaryEmails: 'Puede agregar un máximo de 5 correos secundarios.',
      invalidSecondaryEmail: 'Por favor, ingrese una dirección de correo válida.',
      saveFailed: 'Error al guardar el perfil. Por favor, intente nuevamente.',
      invalidAvatarUrl: 'Por favor, ingrese una URL de avatar válida.'
    }
  },
  profile: {
    key_DISPLAY_NAME: 'Nombre de Mostrar',
    key_NAME: 'Nombre',
    key_EMAIL: 'Correo electrónico',
    key_AFFILIATION: 'Afiliación',
    key_AREA_OF_INTEREST: 'Área de Interés',
    key_HOME_PAGE: 'Página de Inicio',
    key_EMAILS: 'Correos electrónicos',
    key_AVATAR_URL: 'URL del avatar',
    key_ORCID: 'ORCID',
    key_LINKEDIN: 'LinkedIn',
    key_GOOGLE_SCHOLAR: 'Google Scholar'
  },
  profileSetupView: {
    title: 'Configuración del Perfil',
    description: 'Por favor configure su perfil para acceder a este servicio.',
    update: 'Actualizar Perfil',
    editProfile: 'Editar Perfil'
  },
  bankInfoForm: {
    title: 'Agregar/Actualizar información bancaria',
    accountHolder: 'Nombre del titular de la cuenta:',
    iban: 'IBAN:',
    bic: 'Código BIC/Swift:',
    save: 'Guardar información bancaria',
    saved: 'Información bancaria guardada (localmente para esta demo).'
  },
  settings: {
    title: 'Configuración',
    theme: 'Tema',
    light: 'Claro',
    dark: 'Oscuro',
    layout: 'Diseño',
    helpEnabled: 'Mostrar mensajes de ayuda',
    sidebar: 'Barra lateral',
    navbar: 'Barra de navegación',
    save: 'Guardar',
    saved: '¡Configuración guardada!',
    language: 'Idioma',
    experimentations: 'Experimentaciones',
    enabled: 'Activado',
    disabled: 'Desactivado'
  },
  apiKeyModal: {
    title: 'Crear nueva clave API',
    namePlaceholder: 'Nombre para la clave API',
    nameHelp: 'Ingrese un nombre para la clave API, esto le ayudará a identificar la clave más tarde.',
    createButton: 'Crear clave API',
    yourKeyIs: 'Tu nueva clave API es:',
    copyWarning: '¡Copia y guarda esta clave. No podrás verla de nuevo!',
    copy: 'Copiar',
    copied: '¡Copiado!',
    close: 'Cerrar',
    error: 'Error al crear la clave API.'
  },
  lang: {
    en: 'EN',
    fr: 'FR',
    de: 'DE',
    es: 'ES'
  },
  theme: {
    switchToLight: 'Cambiar a modo claro',
    switchToDark: 'Cambiar a modo oscuro'
  }
};
